import React, {
  useCallback, useContext, useEffect, useState
} from "react";
import {
  Button, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, MenuItem, Paper,
  Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography
} from "@material-ui/core";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const emptyForm = {
  code: "",
  discountType: "percent",
  discountValue: "",
  durationMonths: "",
  maxRedemptions: "",
  validFrom: "",
  validUntil: "",
  isActive: true
};

const formatDiscount = coupon =>
  coupon.discountType === "percent"
    ? `${coupon.discountValue}%`
    : Number(coupon.discountValue).toLocaleString(
        "pt-BR", { style: "currency", currency: "BRL" }
      );

const dateLabel = value =>
  value ? value.slice(0, 10).split("-").reverse().join("/") : "Sem prazo";

export default function Coupons() {
  const { user } = useContext(AuthContext);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [permanent, setPermanent] = useState(false);
  const [unlimited, setUnlimited] = useState(false);

  const loadCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coupons");
      setCoupons(Array.isArray(data.coupons) ? data.coupons : []);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.super) loadCoupons();
  }, [user && user.super, loadCoupons]);

  const change = (field, value) =>
    setForm(current => ({ ...current, [field]: value }));

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setPermanent(false);
    setUnlimited(false);
    setDialogOpen(true);
  };

  const openEdit = coupon => {
    setEditingId(coupon.id);
    setForm({
      code: coupon.code || "",
      discountType: coupon.discountType || "percent",
      discountValue: String(coupon.discountValue ?? ""),
      durationMonths: String(coupon.durationMonths ?? ""),
      maxRedemptions: String(coupon.maxRedemptions ?? ""),
      validFrom: coupon.validFrom || "",
      validUntil: coupon.validUntil || "",
      isActive: Boolean(coupon.isActive)
    });
    setPermanent(coupon.durationMonths == null);
    setUnlimited(coupon.maxRedemptions == null);
    setDialogOpen(true);
  };

  const save = async event => {
    event.preventDefault();

    const amount = Number(form.discountValue);
    const duration = Number(form.durationMonths);
    const limit = Number(form.maxRedemptions);

    if (!form.code.trim() || !Number.isFinite(amount) ||
        amount <= 0 || (form.discountType === "percent" && amount >= 100)) {
      toast.error("Confira o código e o valor do desconto.");
      return;
    }

    if ((!permanent && (!Number.isSafeInteger(duration) || duration < 1)) ||
        (!unlimited && (!Number.isSafeInteger(limit) || limit < 1))) {
      toast.error("Informe meses e utilizações maiores que zero.");
      return;
    }

    if (form.validFrom && form.validUntil &&
        form.validUntil < form.validFrom) {
      toast.error("A data final não pode ser anterior à inicial.");
      return;
    }

    const payload = {
      code: form.code.trim().toUpperCase(),
      discountType: form.discountType,
      discountValue: amount,
      durationMonths: permanent ? null : duration,
      maxRedemptions: unlimited ? null : limit,
      validFrom: form.validFrom || null,
      validUntil: form.validUntil || null,
      isActive: form.isActive
    };

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/coupons/${editingId}`, payload);
      } else {
        await api.post("/coupons", payload);
      }
      toast.success(editingId ? "Cupom atualizado!" : "Cupom criado!");
      setDialogOpen(false);
      await loadCoupons();
    } catch (error) {
      const message = error?.response?.data?.error;
      if (typeof message === "string") toast.error(message);
      else toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const toggleCoupon = async coupon => {
    try {
      await api.put(`/coupons/${coupon.id}`, {
        isActive: !coupon.isActive
      });
      toast.success("Status do cupom atualizado.");
      await loadCoupons();
    } catch (error) {
      toastError(error);
    }
  };

  if (!user || !user.super) {
    return (
      <Typography style={{ padding: 24 }}>
        Acesso permitido somente ao Super Admin.
      </Typography>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", gap: 12, marginBottom: 20,
        flexWrap: "wrap"
      }}>
        <div>
          <Typography variant="h5">Cupons de desconto</Typography>
          <Typography variant="body2" color="textSecondary">
            Gerencie descontos comerciais sem alterar os preços dos planos.
          </Typography>
        </div>
        <Button variant="contained" color="primary" onClick={openNew}>
          Novo cupom
        </Button>
      </div>

      <Paper style={{ overflowX: "auto" }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center" }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Desconto</TableCell>
                <TableCell>Duração</TableCell>
                <TableCell>Limite de usos</TableCell>
                <TableCell>Validade</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.map(coupon => (
                <TableRow key={coupon.id}>
                  <TableCell><strong>{coupon.code}</strong></TableCell>
                  <TableCell>{formatDiscount(coupon)}</TableCell>
                  <TableCell>
                    {coupon.durationMonths == null
                      ? "Permanente"
                      : `${coupon.durationMonths} mês(es)`}
                  </TableCell>
                  <TableCell>
                    {coupon.maxRedemptions == null
                      ? "Ilimitado"
                      : coupon.maxRedemptions}
                  </TableCell>
                  <TableCell>{dateLabel(coupon.validUntil)}</TableCell>
                  <TableCell>
                    <Chip size="small"
                      color={coupon.isActive ? "primary" : "default"}
                      label={coupon.isActive ? "Ativo" : "Inativo"}
                    />
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => openEdit(coupon)}>
                      Editar
                    </Button>
                    <Button size="small"
                      onClick={() => toggleCoupon(coupon)}>
                      {coupon.isActive ? "Desativar" : "Ativar"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {coupons.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nenhum cupom cadastrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullWidth maxWidth="sm">
        <form onSubmit={save}>
          <DialogTitle>
            {editingId ? "Editar cupom" : "Criar cupom"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" variant="outlined"
                  label="Código do cupom" value={form.code}
                  onChange={e => change("code", e.target.value)}
                  inputProps={{ maxLength: 64 }} required />
              </Grid>
              <Grid item xs={6}>
                <TextField select fullWidth size="small" variant="outlined"
                  label="Tipo" value={form.discountType}
                  onChange={e => change("discountType", e.target.value)}>
                  <MenuItem value="percent">Percentual (%)</MenuItem>
                  <MenuItem value="fixed">Valor fixo (R$)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" variant="outlined"
                  label="Valor do desconto" type="number"
                  value={form.discountValue}
                  inputProps={{ min: 0.01, step: 0.01 }}
                  onChange={e => change("discountValue", e.target.value)}
                  required />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" variant="outlined"
                  label="Duração (meses)" type="number"
                  disabled={permanent} value={form.durationMonths}
                  inputProps={{ min: 1, step: 1 }}
                  onChange={e => change("durationMonths", e.target.value)} />
                <FormControlLabel control={
                  <Switch checked={permanent}
                    onChange={e => setPermanent(e.target.checked)} />
                } label="Permanente" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" variant="outlined"
                  label="Limite de usos" type="number"
                  disabled={unlimited} value={form.maxRedemptions}
                  inputProps={{ min: 1, step: 1 }}
                  onChange={e => change("maxRedemptions", e.target.value)} />
                <FormControlLabel control={
                  <Switch checked={unlimited}
                    onChange={e => setUnlimited(e.target.checked)} />
                } label="Ilimitado" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" variant="outlined"
                  label="Válido a partir de" type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.validFrom}
                  onChange={e => change("validFrom", e.target.value)} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" variant="outlined"
                  label="Válido até" type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.validUntil}
                  onChange={e => change("validUntil", e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={
                  <Switch checked={form.isActive}
                    onChange={e => change("isActive", e.target.checked)} />
                } label="Cupom ativo" />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}
              disabled={saving}>Cancelar</Button>
            <Button type="submit" color="primary"
              variant="contained" disabled={saving}>
              {saving ? "Salvando..." : "Salvar cupom"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}
